import React, { useState, useMemo, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { renderUrl, urls } from '../../../../config/Url';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import {
  OutlierCategory,
  OutlierContact,
  FilterStatusType,
  OutlierType,
  CCPLinkSupportedCategory,
  CCPLinkLatamSupportedCategory,
  OUTLIER_DEFINITIONS,
} from '../../types/types';
import { getOutlierI18nKey } from '../../helpers';
import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Box from '@amzn/meridian/box';
import { OutlierStatusTag } from '../OutlierStatusTag';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import errorKnockoutTokens from '@amzn/meridian-tokens/base/icon/error-knockout';
import Pagination from '@amzn/meridian/pagination';
import { MAX_ITEMS_PER_PAGE } from '../../types/constants';
import { SortDirection } from '../../../../../src/Types/SortDirection';
import { DEFAULT_LOCALE } from '../../../../Util/Locales';
import {
  getExpanderTabCountDisplay,
  getActionFromStatus,
} from '../../displayString';
import ExpandableCard from '../../../../commons/ExpandableCard';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import PerformancePortalContext from '../../../PerformancePortalContext';
import { OUTLIER_INGRESS } from '../../../FormLibrary/utils/helper';
import { Profile } from '../../../../Types/Profile';
import _ from 'lodash';

interface OutlierTableExpanderProps {
  category: OutlierCategory;
  onActionClick?: (contactId: string) => void;
  pendingCount: number;
  isZeroTolerance?: boolean;
  selectedStatus: FilterStatusType;
  clickstreamTags?: any;
  requiredReviewsRemaining?: number;
  associateRequiredReviews?: Record<string, number>;
  profile: Profile;
}

interface SortParams {
  sortColumn: keyof OutlierContact;
  sortDirection: SortDirection;
}

const OutlierTableExpander: React.FC<OutlierTableExpanderProps> = ({
  category,
  onActionClick,
  pendingCount,
  isZeroTolerance,
  selectedStatus,
  clickstreamTags,
  requiredReviewsRemaining,
  associateRequiredReviews,
  profile,
}) => {
  const isCCPLinkEnabled = isFeatureEnabled(Feature.CCP_LINK);
  const isCCPLinkLatamEnabled = isFeatureEnabled(Feature.CCP_LINK_LATAM);

  // Categories that support CCP Link (includes INVALID_PARK)
  const ccpLinkCategories: CCPLinkSupportedCategory[] = [
    OUTLIER_DEFINITIONS.ASSOCIATE_DISCONNECTS.value,
    OUTLIER_DEFINITIONS.MISSED_FOLLOWUP.value,
  ];

  // Categories that support CCP Link LATAM (excludes INVALID_PARK)
  const ccpLinkLatamCategories: CCPLinkLatamSupportedCategory[] = [
    OUTLIER_DEFINITIONS.ASSOCIATE_DISCONNECTS.value,
    OUTLIER_DEFINITIONS.MISSED_FOLLOWUP.value,
  ];

  const shouldShowCCPLink = (contactCategory: OutlierType): boolean => {
    return (
      (isCCPLinkEnabled &&
        ccpLinkCategories.includes(
          contactCategory as CCPLinkSupportedCategory
        )) ||
      (isCCPLinkLatamEnabled &&
        ccpLinkLatamCategories.includes(
          contactCategory as CCPLinkLatamSupportedCategory
        ))
    );
  };

  const navigate = useNavigate();
  const { clearPerformancePortalContextValue } = useContext(
    PerformancePortalContext
  );

  const uniqueUsers = useMemo(() => {
    return [...new Set(category.contacts.map(contact => contact.user.login))];
  }, [category.contacts]);

  const sortedUsers = useMemo(() => {
    return [...uniqueUsers].sort((a, b) => {
      const aHasPending = category.contacts
        .filter(contact => contact.user.login === a)
        .some(contact => contact.status === ContactStatusOptions.UNDEFINED);

      const bHasPending = category.contacts
        .filter(contact => contact.user.login === b)
        .some(contact => contact.status === ContactStatusOptions.UNDEFINED);

      if (aHasPending && !bHasPending) return -1;
      if (!aHasPending && bHasPending) return 1;
      return 0;
    });
  }, [uniqueUsers, category.contacts]);

  const [sortColumn, setSortColumn] =
    useState<keyof OutlierContact>('contactId');
  const [sortDirection, setSortDirection] = useState<
    SortDirection.ASCENDING | SortDirection.DESCENDING
  >(SortDirection.ASCENDING);

  // Safely access the first element of the array with fallback empty string
  const preselectedUser = _.get(sortedUsers, 0, '');

  const [userSelectedCSA, setUserSelectedCSA] = useState();

  const currentUser = userSelectedCSA ?? preselectedUser;

  const [currentPage, setCurrentPage] = useState(1);
  const [showNoLongerReportsModal, setShowNoLongerReportsModal] =
    useState(false);
  const [selectedContact, setSelectedContact] = useState<OutlierContact | null>(
    null
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [currentUser, category]);

  // Check if a user is in the current direct reports list
  const isUserInCurrentDirectReports = (userLogin: string): boolean => {
    return profile.directReports.some(report => report.userName === userLogin);
  };

  const handleActionClick = (contact: OutlierContact) => {
    onActionClick?.(contact.contactId);
    clearPerformancePortalContextValue();
    navigate(
      renderUrl(
        `${urls.contactReview}/${contact.user.id}/${contact.contactId}`,
        {
          params: {
            ingress: OUTLIER_INGRESS,
            // outlierCalYear: year, // we will not send outlier dates since we confirmed that outlier dates are based on contact dates
            // outlierCalWeek: week, // https://amzn-custserv.slack.com/archives/C0742981R2B/p1746809325122009
          },
        }
      )
    );
  };

  const onSort = ({ sortColumn, sortDirection }: SortParams) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  if (uniqueUsers.length === 0) {
    return null;
  }

  const getCurrentContacts = () => {
    const userContacts = category.contacts.filter(
      contact => contact.user.login === currentUser
    );

    return [...userContacts].sort((a, b) => {
      if (sortColumn !== 'contactId') {
        const aValue = a[sortColumn] ?? '';
        const bValue = b[sortColumn] ?? '';
        return sortDirection === SortDirection.ASCENDING
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      const getStatusPriority = (status: ContactStatusOptions): number => {
        switch (status) {
          case ContactStatusOptions.UNDEFINED:
            return 1;
          case ContactStatusOptions.DRAFT:
            return 2;
          case ContactStatusOptions.DELIVERED:
            return 3;
          case ContactStatusOptions.REVIEWED:
            return 4;
          default:
            return 5;
        }
      };

      const aPriority = getStatusPriority(a.status);
      const bPriority = getStatusPriority(b.status);

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return String(a.contactId).localeCompare(String(b.contactId));
    });
  };

  const getPaginatedContacts = () => {
    const sortedContacts = getCurrentContacts();
    const startIndex = (currentPage - 1) * MAX_ITEMS_PER_PAGE;
    return sortedContacts.slice(startIndex, startIndex + MAX_ITEMS_PER_PAGE);
  };

  const getTotalPages = () => {
    const contacts = getCurrentContacts();
    return Math.max(1, Math.ceil(contacts.length / MAX_ITEMS_PER_PAGE));
  };

  const title = (
    <FormattedMessage
      id={getOutlierI18nKey(category.title)}
      defaultMessage={category.title}
    />
  );

  const titleAddons = (
    <Row
      width='fill'
      alignmentHorizontal={isZeroTolerance ? 'justify' : 'end'}
      alignmentVertical='center'
    >
      {isZeroTolerance && (
        <Row
          alignmentVertical='center'
          spacing='200'
          spacingInset='none none none 200'
        >
          <Icon
            tokens={errorKnockoutTokens}
            color='error'
            data-csa-c-type='icon'
            data-csa-c-content-id='zero-tolerance-indicator'
          />
          <Text type='b200'>
            <FormattedMessage
              id='cs_perfportal_zero_tolerance'
              defaultMessage='Zero Tolerance'
            />
          </Text>
        </Row>
      )}
      <Row alignmentVertical='center'>
        <Text type='h50'>
          <FormattedMessage
            {...getExpanderTabCountDisplay(
              selectedStatus,
              pendingCount,
              requiredReviewsRemaining
            )}
          />
        </Text>
      </Row>
    </Row>
  );

  const expandedContent = (
    <div
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Column spacing='300' spacingInset='300 100 none none' key={currentUser}>
        <TabGroup
          value={currentUser}
          onChange={setUserSelectedCSA}
          type='default'
        >
          {sortedUsers.map(login => (
            <Tab
              key={login}
              value={login}
              data-csa-c-type='tab'
              data-csa-c-slot-id={
                'tm-outlier-management-outlier-category-user-tab-slot'
              }
              data-csa-c-tag1='Coaching'
              data-csa-c-tag2='Outlier Metrics'
              data-csa-c-tag3={category.title}
              data-csa-c-tag4={login}
              {...clickstreamTags}
            >
              <Row alignmentVertical='center' spacing='small'>
                {login}
                {isZeroTolerance &&
                  category.contacts
                    .filter(contact => contact.user.login === login)
                    .some(
                      contact =>
                        contact.status === null ||
                        contact.status === ContactStatusOptions.UNDEFINED
                    )}
              </Row>
            </Tab>
          ))}
        </TabGroup>

        {selectedStatus === ContactStatusOptions.UNDEFINED &&
          associateRequiredReviews &&
          currentUser && (
            <Box spacingInset='300 400 300 400'>
              <Row spacing='large' alignmentVertical='center'>
                <Column>
                  <Row spacing='none'>
                    <Text type='h300'>
                      {associateRequiredReviews[currentUser] || 0}
                    </Text>
                  </Row>
                </Column>
                <Column spacingInset='none none none 400'>
                  <Row spacing='none'>
                    <Text type='h100'>
                      <FormattedMessage
                        id={
                          associateRequiredReviews[currentUser] === 1
                            ? 'cs_perfportal_remaining_review_required_for_associate_singular'
                            : 'cs_perfportal_remaining_reviews_required_for_associate'
                        }
                        defaultMessage={
                          associateRequiredReviews[currentUser] === 1
                            ? 'Remaining review required for @{alias} for {category}'
                            : 'Remaining reviews required for @{alias} for {category}'
                        }
                        values={{
                          alias: currentUser,
                          category: category.title,
                        }}
                      />
                    </Text>
                  </Row>
                </Column>
                <Column spacingInset='none none none 400'>
                  <Row spacing='none'>
                    <Text type='b200'>
                      <FormattedMessage
                        id='cs_perfportal_outlier_weekly_quota_explanation'
                        defaultMessage='Only the required number of reviews per associate count toward your weekly quota. All other pending reviews are optional.'
                      />
                    </Text>
                  </Row>
                </Column>
              </Row>
            </Box>
          )}

        <Box type='outline'>
          <Table
            headerRows={1}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            showDividers={true}
            showStripes={true}
            spacing='small'
          >
            <TableRow>
              <TableCell sortColumn='contactId'>
                <Box>
                  <FormattedMessage
                    id='cs_perfportal_table_contact_id'
                    defaultMessage='Contact ID'
                  />
                </Box>
              </TableCell>
              <TableCell sortColumn='status'>
                <Box>
                  <FormattedMessage
                    id='cs_perfportal_table_status'
                    defaultMessage='Status'
                  />
                </Box>
              </TableCell>
              <TableCell sortColumn='repeatedIssue'>
                <Box>
                  <FormattedMessage
                    id='cs_perfportal_table_repeated_issue'
                    defaultMessage='Repeated issue'
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <FormattedMessage
                    id='cs_perfportal_table_action'
                    defaultMessage='Action'
                  />
                </Box>
              </TableCell>
            </TableRow>
            {getPaginatedContacts().length === 0 ? (
              <TableRow>
                <TableCell>
                  <Box spacingInset='200'>
                    <Text type='b200'>
                      <FormattedMessage
                        id='cs_perfportal_no_contacts'
                        defaultMessage='No contacts found'
                      />
                    </Text>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              getPaginatedContacts().map(contact => {
                const isInCurrentDirectReports = isUserInCurrentDirectReports(
                  contact.user.login
                );
                const action = getActionFromStatus(contact.status);
                return (
                  <TableRow key={contact.contactId}>
                    <TableCell>
                      <Box>{contact.contactId}</Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <OutlierStatusTag status={contact.status} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <FormattedMessage
                          id={
                            contact.repeatedIssue
                              ? 'cs_perfportal_yes'
                              : 'cs_perfportal_no'
                          }
                          defaultMessage={contact.repeatedIssue ? 'Yes' : 'No'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box spacingInset='200'>
                        <Row spacing='350' alignmentVertical='center'>
                          {isInCurrentDirectReports ? (
                            <Button
                              size='small'
                              onClick={() => handleActionClick(contact)}
                              type={
                                contact.status === null ||
                                contact.status ===
                                  ContactStatusOptions.UNDEFINED ||
                                contact.status === ContactStatusOptions.DRAFT
                                  ? 'primary'
                                  : 'secondary'
                              }
                              data-csa-c-type='button'
                              data-csa-c-slot-id={`tm-outlier-management-outlier-${action.defaultMessage.toLowerCase()}-button-slot`}
                              data-csa-c-tag1='Coaching'
                              data-csa-c-tag2='Outlier Metrics'
                              data-csa-c-tag3={category.title}
                              data-csa-c-tag4={contact.user.login}
                              data-csa-c-content-id={contact.contactId}
                              {...clickstreamTags}
                              minWidth='65px'
                            >
                              <FormattedMessage {...action} />
                            </Button>
                          ) : (
                            <Button
                              size='small'
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowNoLongerReportsModal(true);
                                return;
                              }}
                              type='secondary'
                              data-csa-c-type='button'
                              data-csa-c-slot-id={`tm-outlier-management-outlier-${action.defaultMessage.toLowerCase()}-button-slot`}
                              data-csa-c-tag1='Coaching'
                              data-csa-c-tag2='Outlier Metrics'
                              data-csa-c-tag3={category.title}
                              data-csa-c-tag4={contact.user.login}
                              data-csa-c-content-id={contact.contactId}
                              {...clickstreamTags}
                              minWidth='65px'
                            >
                              <FormattedMessage
                                id='cs_perfportal_not_required'
                                defaultMessage='Not Required'
                              />
                            </Button>
                          )}
                          {shouldShowCCPLink(contact.category) && (
                            <Button
                              size='small'
                              href='https://galaxia.amazon.com/ComplianceTMView'
                              target='_blank'
                              rel='noopener noreferrer'
                              disabled={
                                contact.status !==
                                  ContactStatusOptions.REVIEWED &&
                                contact.status !==
                                  ContactStatusOptions.DELIVERED
                              }
                              type='secondary'
                              data-csa-c-type='button'
                              data-csa-c-slot-id={`tm-outlier-management-outlier-dg-action-button-slot`}
                              data-csa-c-tag1='Coaching'
                              data-csa-c-tag2='Outlier Metrics'
                              data-csa-c-tag3={category.title}
                              data-csa-c-tag4={contact.user.login}
                              data-csa-c-content-id={contact.contactId}
                              {...clickstreamTags}
                            >
                              <FormattedMessage
                                id='cs_perfportal_dg_action'
                                defaultMessage='DG Action'
                              />
                            </Button>
                          )}
                        </Row>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </Table>
        </Box>

        {getTotalPages() > 1 && (
          <Row alignmentHorizontal='center'>
            <Pagination
              numberOfPages={getTotalPages()}
              currentPage={currentPage}
              onChange={setCurrentPage}
              showSkipArrows={getTotalPages() > 5}
              locale={DEFAULT_LOCALE}
            />
          </Row>
        )}
      </Column>
    </div>
  );

  return (
    <>
      <Box
        width='fill'
        spacingInset='400 none none none'
        maxWidth='100%'
        overflowX='hidden'
      >
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
          <ExpandableCard
            title={title}
            cardSpacingInset='300 300 350 400'
            titleRowSpacing='200 450 none 200'
            titleAddons={titleAddons}
            expandedContent={expandedContent}
            showDivider={false}
            clickstreamTags={{
              'data-csa-c-type': 'link',
              'data-csa-c-slot-id':
                'tm-outlier-management-outlier-category-link-slot',
              'data-csa-c-tag1': 'Coaching',
              'data-csa-c-tag2': 'Outlier Metrics',
              'data-csa-c-tag3': `${category.title}`,
              ...clickstreamTags,
            }}
          />
        </div>
      </Box>

      <Modal
        open={showNoLongerReportsModal}
        onClose={() => setShowNoLongerReportsModal(false)}
        title={
          <FormattedMessage
            id='cs_perfportal_outlier_no_longer_reports_modal_title'
            defaultMessage='Contact not avaliable for review'
          />
        }
      >
        <Box spacingInset='400'>
          <Text type='b200'>
            <FormattedMessage
              id='cs_perfportal_outlier_no_longer_reports_modal_message'
              defaultMessage='Since {login} no longer reports to you, this contact is not required to review.'
              values={{
                login: selectedContact?.user.login || '',
              }}
            />
          </Text>
        </Box>
        <ModalFooter>
          <Row alignmentHorizontal='end' width='fill'>
            <div style={{ width: '100px' }}>
              <Button
                type='primary'
                onClick={() => setShowNoLongerReportsModal(false)}
              >
                <FormattedMessage
                  id='cs_perfportal_close'
                  defaultMessage='Close'
                />
              </Button>
            </div>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OutlierTableExpander;
